import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mockTickets, statusConfig, priorityConfig, categoryConfig, SupportTicket } from '@/mocks/supportTicketsData';

export default function SupportTicketsPage() {
  const [tickets] = useState<SupportTicket[]>(mockTickets);
  const [filter, setFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved' | 'closed'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const navigate = useNavigate();

  const filtered = tickets.filter((t) => {
    if (filter !== 'all' && t.status !== filter) return false;
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    return true;
  });

  const openCount = tickets.filter((t) => t.status === 'open').length;
  const inProgressCount = tickets.filter((t) => t.status === 'in_progress').length;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffHrs < 1) return 'Agora mesmo';
    if (diffHrs < 24) return `Há ${diffHrs}h`;
    if (diffDays < 7) return `Há ${diffDays} dias`;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  const uniqueCategories = [...new Set(tickets.map((t) => t.category))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100">Suporte</h2>
          <p className="text-sm text-foreground-500 mt-1">
            {openCount + inProgressCount > 0
              ? `${openCount + inProgressCount} chamado${openCount + inProgressCount !== 1 ? 's' : ''} ativo${openCount + inProgressCount !== 1 ? 's' : ''}`
              : 'Nenhum chamado ativo'}
          </p>
        </div>
        <button
          onClick={() => navigate('/support/new')}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line text-lg" />
          Abrir Chamado
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { key: 'all', label: 'Todos', count: tickets.length },
            { key: 'open', label: 'Abertos', count: openCount },
            { key: 'in_progress', label: 'Em Andamento', count: inProgressCount },
            { key: 'resolved', label: 'Resolvidos', count: tickets.filter((t) => t.status === 'resolved').length },
            { key: 'closed', label: 'Fechados', count: tickets.filter((t) => t.status === 'closed').length },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key as typeof filter)}
              className={`px-3.5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                filter === f.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 dark:bg-background-200 text-foreground-600 hover:bg-background-200 dark:hover:bg-background-300'
              }`}
            >
              {f.label}
              {f.count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  filter === f.key ? 'bg-white/20 text-white' : 'bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400'
                }`}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Category filter */}
        <div className="relative">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="appearance-none bg-background-100 dark:bg-background-200 border border-background-200 dark:border-background-300 rounded-full px-4 py-2 pr-8 text-sm text-foreground-700 dark:text-foreground-300 cursor-pointer focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 outline-none"
          >
            <option value="all">Todas categorias</option>
            {uniqueCategories.map((cat) => (
              <option key={cat} value={cat}>{categoryConfig[cat]?.label || cat}</option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
        </div>
      </div>

      {/* Tickets list */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-background-100 dark:bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-customer-service-2-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700 dark:text-foreground-300">Nenhum chamado encontrado</p>
            <p className="text-xs text-foreground-500 mt-1">Use os filtros acima ou abra um novo chamado</p>
          </div>
        ) : (
          filtered.map((ticket) => {
            const status = statusConfig[ticket.status];
            const priority = priorityConfig[ticket.priority];
            const category = categoryConfig[ticket.category] || categoryConfig.general;

            return (
              <Link
                key={ticket.id}
                to={`/support/${ticket.id}`}
                className="block bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4 md:p-5 hover:border-primary-200 dark:hover:border-primary-500/20 transition-all duration-200 group"
              >
                <div className="flex items-start gap-4">
                  {/* Category icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${status.bg}`}>
                    <i className={`${category.icon} text-lg ${status.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold text-foreground-900 dark:text-foreground-100 group-hover:text-primary-600 transition-colors">
                        {ticket.subject}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${status.bg} ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`text-[11px] font-medium ${priority.color}`}>
                        {priority.label}
                      </span>
                      <span className="text-[11px] text-foreground-400">
                        {category.label}
                      </span>
                      <span className="text-[11px] text-foreground-400">
                        #{ticket.id.slice(0, 8)}
                      </span>
                      <span className="text-[11px] text-foreground-400">
                        {formatDate(ticket.updated_at)}
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0 flex items-center">
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors" />
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}